import { autoinject } from 'aurelia-framework';
import { NetlogHttpClient } from 'lib/http';
import { QueryFilter } from 'lib/tables/DataFilter';
import { EntityCreated } from 'models/BaseEntity';
import {
  PluckingListDto,
  CreateMooringProductionOrderDto,
  MooringProductionOrderDto,
  MooringProductionOrderListItemDto,
  UpdateMooringProductionOrderDto,
  SetPluckingListDto,
  AddHoursProductionOrderDto,
  HourProductionOrderDto,
  UpdateHoursProductionOrderDto,
  GridLineProductionItemDto,
  GridLinePluckingDto,
  UpdateGridLineProductionDto,
  MooringProductionOrderStatusPipelineItem,
  UpdateMooringProductionOrderStatusContentQueueDto,
} from 'models/mooring';

@autoinject
export class MooringProductionOrderService {
  constructor(private client: NetlogHttpClient) {}

  async createProductionOrder(dto: CreateMooringProductionOrderDto) {
    return await this.client.post({
      url: `api/mooring-production-orders`,
      model: EntityCreated,
      data: dto,
    });
  }

  async updateProductionOrder(productionOrderId: number, dto: UpdateMooringProductionOrderDto) {
    return await this.client.patch({
      url: `api/mooring-production-orders/${productionOrderId}`,
      data: dto,
    });
  }

  async getProductionOrder(productionOrderId: number) {
    return await this.client.get<MooringProductionOrderDto>({
      url: `api/mooring-production-orders/${productionOrderId}`,
      model: MooringProductionOrderDto,
    });
  }

  async getProductionOrders(query: QueryFilter) {
    return await this.client.get<MooringProductionOrderListItemDto[]>({
      url: `api/mooring-production-orders${query.asQueryString}`,
      model: MooringProductionOrderListItemDto,
    });
  }

  async getBoardData() {
    return await this.client.get<MooringProductionOrderStatusPipelineItem[]>({
      url: `api/mooring-production-orders/boardData`,
      model: MooringProductionOrderStatusPipelineItem,
    });
  }

  async updateBoardDataPipelineContent(statusId: number, data: UpdateMooringProductionOrderStatusContentQueueDto) {
    return await this.client.patch({
      url: `api/mooring-production-orders/boardData/${statusId}/content`,
      data,
    });
  }

  // Plucking
  async getAnchorLinesPluckingList(productionOrderId: number) {
    return await this.client.get<PluckingListDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/anchor-lines`,
      model: PluckingListDto,
    });
  }

  async setAnchorLinesPluckingList(productionOrderId: number, dto: SetPluckingListDto[]) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/anchor-lines`,
      data: dto,
    });
  }

  async getFleetAnchorLinesPluckingList(productionOrderId: number) {
    return await this.client.get<PluckingListDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/fleet-anchor-lines`,
      model: PluckingListDto,
    });
  }

  async setFleetAnchorLinesPluckingList(productionOrderId: number, dto: SetPluckingListDto[]) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/fleet-anchor-lines`,
      data: dto,
    });
  }

  async getBuoysPluckingList(productionOrderId: number) {
    return await this.client.get<PluckingListDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/buoys`,
      model: PluckingListDto,
    });
  }

  async setBuoysPluckingList(productionOrderId: number, dto: SetPluckingListDto[]) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/buoys`,
      data: dto,
    });
  }

  async getProductionGridLines(productionOrderId: number) {
    return await this.client.get<GridLineProductionItemDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/grid-lines`,
      model: GridLineProductionItemDto,
    });
  }

  async getGridLinesPluckingList(productionOrderId: number) {
    return await this.client.get<GridLinePluckingDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/grid-lines`,
      model: GridLinePluckingDto,
    });
  }

  async setGridLineProduction({
    dto,
    gridLineId,
    productionOrderId,
  }: {
    productionOrderId: number;
    gridLineId: number;
    dto: UpdateGridLineProductionDto;
  }) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/grid-lines/${gridLineId}`,
      data: dto,
    });
  }

  async setGridLinePluckList(productionOrderId: number, dto: SetPluckingListDto[]) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/grid-lines`,
      data: dto,
    });
  }

  async getBridlesPluckingList(productionOrderId: number) {
    return await this.client.get<PluckingListDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/bridles`,
      model: PluckingListDto,
    });
  }

  async setBridlesPluckingList(productionOrderId: number, dto: SetPluckingListDto[]) {
    return await this.client.put({
      url: `api/mooring-production-orders/${productionOrderId}/plucking/bridles`,
      data: dto,
    });
  }

  async getHours(productionOrderId: number) {
    return await this.client.get<HourProductionOrderDto[]>({
      url: `api/mooring-production-orders/${productionOrderId}/hours`,
      model: HourProductionOrderDto,
    });
  }

  async getHour(productionOrderId: number, hourId: number) {
    return await this.client.get<HourProductionOrderDto>({
      url: `api/mooring-production-orders/${productionOrderId}/hours/${hourId}`,
      model: HourProductionOrderDto,
    });
  }

  async addHour(productionOrderId: number, dto: AddHoursProductionOrderDto) {
    return await this.client.post({
      url: `api/mooring-production-orders/${productionOrderId}/hours`,
      data: dto,
    });
  }

  async deleteHour(productionOrderId: number, hourId: number) {
    return await this.client.delete({
      url: `api/mooring-production-orders/${productionOrderId}/hours/${hourId}`,
    });
  }

  async updateHour(productionOrderId: number, hourId: number, dto: UpdateHoursProductionOrderDto) {
    return await this.client.patch({
      url: `api/mooring-production-orders/${productionOrderId}/hours/${hourId}`,
      data: dto,
    });
  }
}
